/**
 * ASCII Art Generator - React Component 导出器
 *
 * 导出可嵌入 React 项目的 ASCII 艺术背景组件
 * Wave 6 完整实现
 * Wave 4 Phase 6.5: 引用完整打包运行时
 */

import type { ReactExportConfig, AsciiBgRuntimeConfig } from './types'
import { DEFAULT_REACT_EXPORT_CONFIG } from './types'
import type { FullRenderSettings } from '../types'
import type { EffectsState } from '../effects/types'
import { createAsciiRuntimeConfig } from './runtime-config'
import { getRuntimeContent, imageToDataURL, getImageDimensions, extractEnabledEffects } from './utils'

// ============================================
// React 导出结果
// ============================================

export interface ReactExportOutput {
  /** 组件代码内容 */
  content: string
  /** 外部运行时 JS 文件内容（分离模式） */
  externalJsContent?: string
  /** 外部运行时 JS 文件名 */
  externalJsFilename?: string
  /** 需要下载完整运行时（分离模式） */
  needsRuntimeDownload?: boolean
  /** 类型定义文件内容（分离模式） */
  typesContent?: string
  /** 文件名 */
  filename: string
}

// ============================================
// 运行时版本信息
// ============================================

const RUNTIME_VERSION = '1.0.0'
const RUNTIME_FILENAME = 'ascii-bg-runtime.js'

// ============================================
// React 导出器类
// ============================================

/**
 * React Component 导出器
 */
export class ReactExporter {
  /**
   * 导出 React 组件
   *
   * @param sourceImage 源图片
   * @param settings 渲染设置
   * @param effectsState 特效状态
   * @param config 导出配置
   */
  async export(
    sourceImage: HTMLImageElement | string,
    settings: FullRenderSettings,
    effectsState: EffectsState,
    config: Partial<ReactExportConfig> = {}
  ): Promise<ReactExportOutput> {
    const finalConfig = { ...DEFAULT_REACT_EXPORT_CONFIG, ...config }

    // 从 settings 和 effectsState 生成运行时配置
    const runtimeConfig = this.createRuntimeConfig(settings, effectsState, finalConfig, sourceImage)

    // 获取图片数据
    const imageData = await this.getImageData(sourceImage, finalConfig)

    // 获取完整运行时内容
    const runtimeContent = await getRuntimeContent()

    // 根据模式生成组件
    if (finalConfig.splitRuntime) {
      return this.generateSplitComponent(finalConfig, runtimeConfig, imageData, runtimeContent)
    } else {
      return this.generateInlineComponent(finalConfig, runtimeConfig, imageData, runtimeContent)
    }
  }

  /**
   * 从设置创建运行时配置
   */
  private createRuntimeConfig(
    settings: FullRenderSettings,
    effectsState: EffectsState,
    exportConfig: ReactExportConfig,
    sourceImage?: HTMLImageElement | string
  ): AsciiBgRuntimeConfig {
    return createAsciiRuntimeConfig(settings, effectsState, exportConfig, sourceImage)

    // 使用共享工具函数提取启用的特效
    const { enabledEffects, effectsConfig } = extractEnabledEffects(effectsState)

    // 获取图片尺寸
    const dims = getImageDimensions(sourceImage || '')
    const imageWidth = dims.width
    const imageHeight = dims.height

    return {
      engine: settings?.style || exportConfig.engine,
      animation: settings?.animationType || exportConfig.animation,
      animationParams: {
        amplitude: settings?.ditherWaveAmplitude || 0.3,
        frequency: settings?.ditherWaveFrequency || 3,
        speed: settings?.ditherWaveSpeed || 0.05
      },
      effects: enabledEffects,
      effectsConfig: effectsConfig,
      transparent: exportConfig.transparent,
      alphaMask: exportConfig.alphaMask,
      pauseOffScreen: exportConfig.pauseOffScreen,
      interaction: exportConfig.interaction || settings?.ditherMouseInteractionEnabled || settings?.dither2MouseInteractionEnabled || false,
      fadeIn: exportConfig.fadeIn,
      adaptivePerf: exportConfig.adaptivePerf,
      targetFPS: 60,

      // === 渲染参数（所见即所得） ===
      fontSize: settings?.fontSize || 10,
      charSpacing: settings?.charSpacing || 1,
      contrast: settings?.contrast || 1,
      brightness: settings?.brightness || 0,
      opacity: settings?.opacity || 1,
      invert: settings?.invert || false,
      backgroundColor: settings?.backgroundColor || '#000000',
      colorMode: settings?.colorMode || 'grayscale',
      customColor: settings?.customColor || '#00ff99',
      ditherStrength: settings?.ditherStrength || 0.8,
      halftoneSize: settings?.halftoneSize || 1,
      particleDensity: settings?.particleDensity || 0.5,
      claudeDensity: settings?.claudeDensity || 0.7,
      retroNoise: settings?.retroNoise || 0.45,
      lineLength: settings?.lineLength || 1,
      lineThickness: settings?.lineThickness || 1.6,

      // 字符集参数
      charset: settings?.charset,
      customCharset: settings?.customCharset,
      brailleVariant: settings?.brailleVariant,
      terminalCharset: settings?.terminalCharset,
      renderFont: settings?.renderFont,

      // 图片比例
      imageWidth,
      imageHeight,

      // === 鼠标交互参数（所见即所得） ===
      mouseInteractionMode: settings?.mouseInteractionMode || 'attract',
      ditherMouseRadius: settings?.ditherMouseRadius || 0.3,
      ditherMouseInteractionEnabled: settings?.ditherMouseInteractionEnabled || false,
      dither2MouseInteractionEnabled: settings?.dither2MouseInteractionEnabled || false,
      hoverStrength: settings?.hoverStrength || 24,
      mouseAreaSize: settings?.mouseAreaSize || 180,
      mouseSpread: settings?.mouseSpread || 1,

      // === 旧版兼容参数名（成功版本的运行时） ===
      animationEnabled: settings?.ditherAnimationEnabled || settings?.dither2AnimationEnabled || false,
      mouseInteractionEnabled: settings?.ditherMouseInteractionEnabled || settings?.dither2MouseInteractionEnabled || false,
      mouseInfluenceRadius: settings?.mouseAreaSize || 180,
      mouseInfluenceStrength: settings?.mouseSpread || 1
    }
  }

  /**
   * 获取图片数据
   */
  private async getImageData(
    sourceImage: HTMLImageElement | string,
    _config: ReactExportConfig
  ): Promise<string> {
    return imageToDataURL(sourceImage, 0.85)
  }

  /**
   * 生成 Props 类型定义
   */
  private generatePropsTypes(componentName: string, _config: ReactExportConfig): string {
    return `interface ${componentName}Props {
  /** 图片源（URL 或 Base64） */
  image?: string;

  /** 渲染引擎类型 */
  engine?: 'classic' | 'braille' | 'halftone' | 'dither' | 'dither2' | 'dotcross' | 'line' | 'particles' | 'claude' | 'retro' | 'terminal';

  /** 动画类型 */
  animation?: 'wave' | 'pulse' | 'jitter' | 'drift' | 'breathe' | 'blink' | 'rainbow' | 'loop' | 'comet' | 'stream' | 'none';

  /** 透明背景 */
  transparent?: boolean;

  /** Alpha 遮罩渐变 */
  alphaMask?: boolean;

  /** 离屏暂停 */
  pauseOffScreen?: boolean;

  /** 鼠标交互 */
  interaction?: boolean;

  /** 淡入效果 */
  fadeIn?: boolean;

  /** 自适应性能 */
  adaptivePerf?: boolean;

  /** 目标帧率 */
  targetFPS?: number;

  /** 自定义 CSS 类名 */
  className?: string;

  /** 自定义样式 */
  style?: React.CSSProperties;

  /** 子元素（作为内容层） */
  children?: React.ReactNode;

  /** 初始化完成回调 */
  onReady?: (instance: { start: () => void; pause: () => void; resume: () => void; destroy: () => void; isRunning: () => boolean }) => void;

  /** 错误回调 */
  onError?: (error: string) => void;
}`
  }

  /**
   * 生成内联组件（完整运行时内嵌）
   */
  private generateInlineComponent(
    config: ReactExportConfig,
    runtimeConfig: AsciiBgRuntimeConfig,
    imageData: string,
    runtimeContent: string
  ): ReactExportOutput {
    const componentName = config.componentName

    const runtimeWarning = !runtimeContent
      ? `// ⚠️ 警告：运行时内容获取失败，请确保已构建运行时 (npm run build:runtime)`
      : ''

    const componentCode = `/**
 * ${componentName} - ASCII Art Background Component
 *
 * 内嵌完整运行时：支持 11 引擎+12 动画+8 特效
 * 生成时间：${new Date().toISOString()}
 */

import React, { useRef, useEffect, useState, useCallback } from 'react';

// ============================================
// Props 类型定义
// ============================================

${this.generatePropsTypes(componentName, config)}

// ============================================
// 默认配置
// ============================================

const DEFAULT_CONFIG = {
  engine: '${config.engine}',
  animation: '${config.animation}',
  transparent: ${config.transparent},
  alphaMask: ${config.alphaMask},
  pauseOffScreen: ${config.pauseOffScreen},
  interaction: ${config.interaction},
  fadeIn: ${config.fadeIn},
  adaptivePerf: ${config.adaptivePerf},
  targetFPS: 60
};

// 默认图片数据（Base64 嵌入）
const DEFAULT_IMAGE = "${imageData}";

// 默认运行时配置（从导出器生成）
const DEFAULT_RUNTIME_CONFIG = ${JSON.stringify(runtimeConfig)};

// ============================================
// 内嵌完整运行时
// ============================================

${runtimeWarning}
${runtimeContent}

// ============================================
// React 组件
// ============================================

const ${componentName}: React.FC<${componentName}Props> = ({
  image = DEFAULT_IMAGE,
  engine,
  animation,
  transparent,
  alphaMask,
  pauseOffScreen,
  interaction,
  fadeIn,
  adaptivePerf,
  targetFPS,
  className,
  style,
  children,
  onReady,
  onError,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const instanceRef = useRef<any>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const config = {
    ...DEFAULT_RUNTIME_CONFIG,
    ...(engine !== undefined && { engine }),
    ...(animation !== undefined && { animation }),
    ...(transparent !== undefined && { transparent }),
    ...(alphaMask !== undefined && { alphaMask }),
    ...(pauseOffScreen !== undefined && { pauseOffScreen }),
    ...(interaction !== undefined && { interaction }),
    ...(fadeIn !== undefined && { fadeIn }),
    ...(adaptivePerf !== undefined && { adaptivePerf }),
    ...(targetFPS !== undefined && { targetFPS }),
  };

  useEffect(() => {
    if (!containerRef.current || !image) return;

    let mounted = true;

    // 使用内嵌的完整运行时
    const initPromise = window.AsciiBg
      ? window.AsciiBg.init(containerRef.current!, config, image)
      : Promise.reject(new Error('Runtime not loaded'));

    initPromise
      .then((instance) => {
        if (mounted) {
          instanceRef.current = instance;
          setIsLoaded(true);
          setError(null);
          onReady?.(instance);
        }
      })
      .catch((e) => {
        if (mounted) {
          const errorMsg = e instanceof Error ? e.message : 'Failed to initialize';
          setError(errorMsg);
          onError?.(errorMsg);
        }
      });

    return () => {
      mounted = false;
      if (instanceRef.current) {
        instanceRef.current.destroy();
        instanceRef.current = null;
      }
    };
  }, [image]);

  useEffect(() => {
    if (instanceRef.current) {
      instanceRef.current.updateConfig?.(config);
    }
  }, [engine, animation, transparent, alphaMask, pauseOffScreen, interaction, fadeIn, adaptivePerf, targetFPS]);

  // 暂停/恢复（响应 pauseOffScreen 变化）
  useEffect(() => {
    if (!pauseOffScreen || !instanceRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            instanceRef.current?.resume();
          } else {
            instanceRef.current?.pause();
          }
        });
      },
      { threshold: 0.1 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, [pauseOffScreen]);

  const pause = useCallback(() => {
    instanceRef.current?.pause();
  }, []);

  const resume = useCallback(() => {
    instanceRef.current?.resume();
  }, []);

  return (
    <div
      ref={containerRef}
      className={className}
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        background: '#000',
        ...style,
      }}
    >
      {!isLoaded && !error && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          color: '#888',
        }}>
          Loading...
        </div>
      )}

      {error && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          color: '#f44',
        }}>
          Error: {error}
        </div>
      )}

      {children && (
        <div style={{
          position: 'relative',
          zIndex: 10,
        }}>
          {children}
        </div>
      )}
    </div>
  );
};

export default ${componentName};
export { ${componentName} };
export type { ${componentName}Props };

/**
 * 使用示例：
 *
 * import ${componentName} from './${componentName}';
 *
 * function App() {
 *   return (
 *     <div style={{ height: '100vh' }}>
 *       <${componentName}>
 *         <h1>Your Content Here</h1>
 *       </${componentName}>
 *     </div>
 *   );
 * }
 */
`

    return {
      content: componentCode,
      filename: `${componentName}.tsx`
    }
  }

  /**
   * 生成分离组件（外部运行时 JS 文件）
   *
   * HTML 引用外部运行时，ZIP 打包时自动包含完整运行时
   */
  private generateSplitComponent(
    config: ReactExportConfig,
    runtimeConfig: AsciiBgRuntimeConfig,
    imageData: string,
    runtimeContent: string
  ): ReactExportOutput {
    const componentName = config.componentName

    const componentCode = `/**
 * ${componentName} - ASCII Art Background Component
 *
 * 使用外部运行时文件：ascii-bg-runtime.js
 * 生成时间：${new Date().toISOString()}
 */

import React, { useRef, useEffect, useState } from 'react';

// ============================================
// Props 类型定义
// ============================================

${this.generatePropsTypes(componentName, config)}

// ============================================
// 默认配置
// ============================================

const DEFAULT_CONFIG = {
  engine: '${config.engine}',
  animation: '${config.animation}',
  transparent: ${config.transparent},
  alphaMask: ${config.alphaMask},
  pauseOffScreen: ${config.pauseOffScreen},
  interaction: ${config.interaction},
  fadeIn: ${config.fadeIn},
  adaptivePerf: ${config.adaptivePerf},
  targetFPS: 60
};

// 默认图片数据（Base64 嵌入）
const DEFAULT_IMAGE = "${imageData}";

// 默认运行时配置
const DEFAULT_RUNTIME_CONFIG = ${JSON.stringify(runtimeConfig)};

declare global {
  interface Window {
    AsciiBg?: {
      init: (container: HTMLElement, config: Record<string, unknown>, image?: string) => Promise<any>;
      destroy: (container: HTMLElement) => void;
    };
  }
}

function loadAsciiBgRuntime(): Promise<void> {
  if (window.AsciiBg) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>('script[data-ascii-bg-runtime]');
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('Failed to load ascii-bg-runtime.js')), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = './ascii-bg-runtime.js';
    script.async = true;
    script.dataset.asciiBgRuntime = 'true';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load ascii-bg-runtime.js'));
    document.head.appendChild(script);
  });
}

// ============================================
// React 组件
// ============================================

const ${componentName}: React.FC<${componentName}Props> = ({
  image = DEFAULT_IMAGE,
  engine,
  animation,
  transparent,
  alphaMask,
  pauseOffScreen,
  interaction,
  fadeIn,
  adaptivePerf,
  targetFPS,
  className,
  style,
  children,
  onReady,
  onError,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const instanceRef = useRef<any>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const config = {
    ...DEFAULT_RUNTIME_CONFIG,
    ...(engine !== undefined && { engine }),
    ...(animation !== undefined && { animation }),
    ...(transparent !== undefined && { transparent }),
    ...(alphaMask !== undefined && { alphaMask }),
    ...(pauseOffScreen !== undefined && { pauseOffScreen }),
    ...(interaction !== undefined && { interaction }),
    ...(fadeIn !== undefined && { fadeIn }),
    ...(adaptivePerf !== undefined && { adaptivePerf }),
    ...(targetFPS !== undefined && { targetFPS }),
  };

  useEffect(() => {
    if (!containerRef.current || !image) return;

    let mounted = true;

    loadAsciiBgRuntime()
      .then(() => {
        if (!containerRef.current || !window.AsciiBg) {
          throw new Error('Runtime not loaded');
        }
        return window.AsciiBg.init(containerRef.current, config, image);
      })
      .then((instance) => {
        if (mounted) {
          instanceRef.current = instance;
          setIsLoaded(true);
          setError(null);
          onReady?.(instance);
        }
      })
      .catch((e) => {
        if (mounted) {
          const errorMsg = e instanceof Error ? e.message : 'Failed to initialize';
          setError(errorMsg);
          onError?.(errorMsg);
        }
      });

    return () => {
      mounted = false;
      if (containerRef.current && window.AsciiBg) {
        window.AsciiBg.destroy(containerRef.current);
      }
    };
  }, [image]);

  useEffect(() => {
    if (instanceRef.current) {
      instanceRef.current.updateConfig?.(config);
    }
  }, [engine, animation, transparent, alphaMask, pauseOffScreen, interaction, fadeIn, adaptivePerf, targetFPS]);

  return (
    <div
      ref={containerRef}
      className={className}
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        background: '#000',
        ...style,
      }}
    >
      {!isLoaded && !error && (
        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', color: '#888' }}>
          Loading...
        </div>
      )}
      {error && (
        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', color: '#f44' }}>
          Error: {error}
        </div>
      )}
      {children && (
        <div style={{ position: 'relative', zIndex: 10 }}>
          {children}
        </div>
      )}
    </div>
  );
};

export default ${componentName};
export { ${componentName} };
export type { ${componentName}Props };

/**
 * 使用说明：
 *
 * 1. 将此组件文件和 ascii-bg-runtime.js 放在同一目录
 * 2. 运行时包含完整功能：11 引擎 + 12 动画 + 8 特效
 * 3. 图片数据已嵌入组件，可通过 props 更换
 */
`

    // 生成类型定义文件
    const typesContent = config.includeTypes
      ? `/**
 * ASCII Background Runtime - Type Definitions
 */

export interface AsciiBgConfig {
  engine?: string;
  animation?: string;
  transparent?: boolean;
  alphaMask?: boolean;
  pauseOffScreen?: boolean;
  interaction?: boolean;
  fadeIn?: boolean;
  adaptivePerf?: boolean;
  targetFPS?: number;
}

export interface AsciiBgInstance {
  start(): void;
  pause(): void;
  resume(): void;
  destroy(): void;
  isRunning(): boolean;
  updateConfig(config: Partial<AsciiBgConfig>): void;
}

export function initAsciiBg(
  selector: string | HTMLElement,
  config: AsciiBgConfig,
  imageSrc?: string
): Promise<AsciiBgInstance>;

export function destroyAsciiBg(selector: string | HTMLElement): void;

export function getAsciiBg(selector: string | HTMLElement): AsciiBgInstance | undefined;

export function destroyAllAsciiBg(): void;
`
      : undefined

    return {
      content: componentCode,
      externalJsContent: runtimeContent, // ZIP 打包时使用
      externalJsFilename: RUNTIME_FILENAME,
      needsRuntimeDownload: !runtimeContent, // 如果运行时为空，标记需要下载
      typesContent,
      filename: `${componentName}.tsx`
    }
  }

  /**
   * 清除运行时缓存
   */
  clearCache(): void {
    // 使用共享工具函数的缓存清除
  }

  /**
   * 获取完整运行时信息
   */
  getRuntimeInfo(): { filename: string; version: string; size: string; features: string[] } {
    return {
      filename: RUNTIME_FILENAME,
      version: RUNTIME_VERSION,
      size: '~125KB (gzip ~30KB)',
      features: [
        '11 渲染引擎',
        '12 动画效果',
        '8 叠加特效',
        '离屏暂停',
        '自适应性能监控',
        'Alpha 遮罩渐变',
        '鼠标交互'
      ]
    }
  }
}

// ============================================
// 工厂函数
// ============================================

export function createReactExporter(): ReactExporter {
  return new ReactExporter()
}
